import { Names } from '@mode-switch/model';
import { EMPTY_ENCODING, VariableType } from '@mode-vegas/vegas';
import { EncodingHelpers, SwitchRenderModel, SwitchSchemaModel, makeSummaryData } from '@mode/charts/util-vegas';
import { FLAMINGO_RESERVED_REFS } from '@mode/shared/util-js';
import { createSelector, createStructuredSelector } from 'reselect';
import { DomainAppState } from '../charts-domain.types';
import * as fromChart from './chart.reducer';

// @TODO: (@anthonysimone) [GF] - Once the charts facade is built out, refactor the direct usage
// of these selectors out of this file and implement the observable creation in the charts.facade
// eslint-disable-next-line @nx/enforce-module-boundaries
import { fromFlamingoSelectors, fromSwitchViewSelectors } from '@mode/switch-views/domain';

// This is the original NgRx selector
// export const selectChartState = createFeatureSelector<ChartTypes.ChartFeatureState>(fromChart.chartFeatureKey);

// Using reselect because ngrx createSelector doesn't seem to memoize correctly for us
export const selectChartState = (state: DomainAppState) => state[fromChart.chartFeatureKey];

export const selectedToken = createSelector(selectChartState, (chart) => chart.selectedToken);
export const selectResources = createSelector(selectChartState, (chart) => chart.resources);
export const selectHistories = createSelector(selectChartState, (chart) => chart.histories);
export const selectQueryTokens = createSelector(selectChartState, (chart) => chart.queryTokens);
export const selectSwitchViewTokens = createSelector(selectChartState, (chart) => chart.switchViewTokens);
export const selectEncodings = createSelector(selectChartState, (chart) => chart.encodings);

export const selectFormats = createSelector(selectChartState, (chart) => chart.formats);

export const selectFieldFormats = createSelector(selectChartState, (chart) => chart.fieldFormats);
export const selectFieldColors = createSelector(selectChartState, (chart) => chart.fieldColors);
export const selectFacetSizes = createSelector(selectChartState, (chart) => chart.facetSizes);
export const selectChartTypes = createSelector(selectChartState, (chart) => chart.chartTypes);
export const selectTitles = createSelector(selectChartState, (chart) => chart.titles);
export const selectDescriptions = createSelector(selectChartState, (chart) => chart.descriptions);
export const selectCaptions = createSelector(selectChartState, (chart) => chart.captions);
export const selectIsAutoUpdateDisabled = createSelector(selectChartState, (chart) => chart.isAutoUpdateDisabled);
export const selectManuallyApplyChanges = createSelector(selectChartState, (chart) => chart.manuallyApplyChanges);
export const selectUnappliedChanges = createSelector(selectChartState, (chart) => chart.unappliedChangesCount);
export const selectEphemerals = createSelector(selectChartState, (chart) => chart.ephemerals);
export const selectTablePaging = createSelector(selectChartState, (chart) => chart.tablePaging);
export const selectTableColumnSearch = createSelector(selectChartState, (chart) => chart.tableColumnSearch);
export const selectTokens = createSelector(selectChartState, (chart) => chart.tokens);
export const selectV1Views = createSelector(selectChartState, (chart) => chart.v1Views);
export const selectVersions = createSelector(selectChartState, (chart) => chart.versions);
export const selectColorPaletteTokens = createSelector(selectChartState, (chart) => chart.colorPaletteTokens);
export const selectPersistableTokens = createSelector(selectTokens, (tokens) =>
  tokens.filter((t) => t !== FLAMINGO_RESERVED_REFS.ChartExplorer)
);
export const selectCreatedTimestamps = createSelector(selectChartState, (chart) => chart.createdTimestamps);

export const selectSelectedFieldToFormat = createSelector(selectChartState, (chart) => {
  return chart.selectedFieldToFormat;
});

export function createChartExistsSelector(chartToken: string) {
  return createSelector(selectTokens, (tokens) => tokens.get(chartToken));
}

export function createQueryTokenSelector(chartToken: string) {
  return createSelector(selectQueryTokens, (tokens) => tokens.get(chartToken));
}

export function createSwitchViewTokenSelector(chartToken: string) {
  return createSelector(selectSwitchViewTokens, (tokens) => tokens.get(chartToken));
}

export function createChartTypeSelector(chartToken: string) {
  return createSelector(selectChartTypes, (types) => types.get(chartToken));
}

export function createV1ViewSelector(chartToken: string) {
  return createSelector(selectV1Views, (views) => views.get(chartToken));
}

export function createChartVersionSelector(chartToken: string) {
  return createSelector(selectVersions, (versions) => versions.get(chartToken));
}

export function createChartTitleSelector(chartToken: string) {
  return createSelector(selectTitles, (titles) => titles.get(chartToken));
}

export function createChartDescriptionSelector(chartToken: string) {
  return createSelector(selectDescriptions, (descriptions) => descriptions.get(chartToken));
}

export function createChartFormatSelector(chartToken: string) {
  return createSelector(selectFormats, (formats) => formats.get(chartToken));
}

export function createChartFieldFormatMapSelector(chartToken: string) {
  return createSelector(selectFieldFormats, (formats) => formats.get(chartToken));
}

export function createChartFieldColorMapSelector(chartToken: string) {
  return createSelector(selectFieldColors, (fieldColors) => fieldColors.get(chartToken));
}

export function createChartFacetSizeMapSelector(chartToken: string) {
  return createSelector(selectFacetSizes, (facetSizes) => facetSizes.get(chartToken));
}

export function createCaptionSelector(chartToken: string) {
  return createSelector(selectCaptions, (caption) => caption.get(chartToken));
}

export function createIsAutoUpdateDisabledSelector(chartToken: string) {
  return createSelector(
    selectIsAutoUpdateDisabled,
    (isAutoUpdateDisabled) => isAutoUpdateDisabled.get(chartToken) ?? false
  );
}

export function createManuallyApplyChangesSelector(chartToken: string) {
  return createSelector(selectManuallyApplyChanges, (applyChanges) => applyChanges.get(chartToken) ?? false);
}

export function createUnappliedChangesSelector(chartToken: string) {
  return createSelector(selectUnappliedChanges, (unappliedChanges) => unappliedChanges.get(chartToken) ?? 0);
}

export function createEphemeralsSelector(chartToken: string) {
  return createSelector(selectEphemerals, (ephemerals) => ephemerals.get(chartToken) ?? false);
}

export function createEncodingSelector(chartToken: string) {
  return createSelector(selectEncodings, (encodings) => encodings.get(chartToken));
}

export function createPaletteSelector(chartToken: string) {
  return createSelector(selectColorPaletteTokens, (tokens) => tokens.get(chartToken));
}

export function createHistorySelector(chartToken: string) {
  return createSelector(selectHistories, (histories) => histories.get(chartToken));
}

export function createTablePagingSelector(chartToken: string) {
  return createSelector(selectTablePaging, (paging) => paging[chartToken]);
}

export function createTableColumnSearchSelector(chartToken: string) {
  return createSelector(selectTableColumnSearch, (search) => search[chartToken]);
}

export function createChartResourceSelector(chartToken: string) {
  return createSelector(selectResources, (resources) => resources.get(chartToken) || null);
}

export function createUndoStateSelector(chartToken: string) {
  return createSelector(createHistorySelector(chartToken), (history) => history?.undoStack[0]?.state);
}

export function createRedoStateSelector(chartToken: string) {
  return createSelector(createHistorySelector(chartToken), (history) => history?.redoStack[0]?.state);
}

export function createV1orV2ChartTypeSelector(chartToken: string) {
  return createSelector(
    createChartVersionSelector(chartToken),
    createChartTypeSelector(chartToken),
    createV1ViewSelector(chartToken),
    (version, v2ChartType, v1View) => (version === 1 ? v1View?.selectedChart : v2ChartType)
  );
}

export function createPagingOffsetSelector(chartToken: string) {
  return createSelector(createTablePagingSelector(chartToken), (paging) => {
    if (paging) {
      return paging.offset;
    }

    return 0;
  });
}

export function createColumnSearchFilterSelector(chartToken: string) {
  return createSelector(selectTableColumnSearch, (search) => search[chartToken] || {});
}

export function createChartTypeEncodingSelector(chartToken: string) {
  return createSelector(
    createChartTypeSelector(chartToken),
    createQueryTokenSelector(chartToken),
    createEncodingSelector(chartToken),
    createChartVersionSelector(chartToken),
    (chartType, queryToken, encoding, version) => {
      // return nothing if not a v2 chart because encoding is not applicable
      if (chartType != null && queryToken != null && encoding != null && version === 2) {
        return {
          chartType,
          chartToken,
          queryToken,
          encoding,
        };
      }

      return null;
    }
  );
}

export function createIsAutoOrManualUpdateChartSelector(chartToken: string) {
  return createSelector(
    createIsAutoUpdateDisabledSelector(chartToken),
    createManuallyApplyChangesSelector(chartToken),
    (isAutoUpdateDisabled, manuallyApplyChanges) => !isAutoUpdateDisabled || manuallyApplyChanges
  );
}

/**
 * For retrieving essential state of a chart for use in effects or to pass to a reducer via an action.
 * Common case is to support existing undo/redo implementation. Some things are excluded since they
 * never change.
 */
export function createChartSnapshotSelector(chartToken: string) {
  return createSelector(
    createEncodingSelector(chartToken),
    createPaletteSelector(chartToken),
    createChartFormatSelector(chartToken),
    createChartFieldFormatMapSelector(chartToken),
    createChartFieldColorMapSelector(chartToken),
    createChartFacetSizeMapSelector(chartToken),
    createChartTitleSelector(chartToken),
    createChartDescriptionSelector(chartToken),
    createCaptionSelector(chartToken),
    createChartTypeSelector(chartToken),
    createIsAutoUpdateDisabledSelector(chartToken),
    (
      encoding,
      colorPaletteToken,
      format,
      fieldFormats,
      fieldColors,
      facetSizes,
      title,
      description,
      caption,
      chartType,
      isAutoUpdateDisabled
    ) => ({
      encoding,
      colorPaletteToken,
      format,
      fieldFormats,
      fieldColors,
      facetSizes,
      title,
      description,
      caption,
      chartType,
      isAutoUpdateDisabled,
    })
  );
}

export function createFlamingoParamsSelector(chartToken: string) {
  return createSelector(
    createChartTypeSelector(chartToken),
    createSwitchViewTokenSelector(chartToken),
    createEncodingSelector(chartToken),
    createChartVersionSelector(chartToken),
    createIsAutoUpdateDisabledSelector(chartToken),
    createManuallyApplyChangesSelector(chartToken),
    (chartType, switchViewToken, encoding, version, isAutoUpdateDisabled, manuallyApplyChanges) => {
      // return nothing if not a v2 chart because encoding is not applicable
      if (chartType != null && switchViewToken != null && encoding != null && version === 2) {
        return {
          chartType,
          chartToken,
          switchViewToken,
          encoding,
          isAutoUpdateDisabled,
          manuallyApplyChanges,
        };
      }

      return null;
    }
  );
}

export function createIsCompleteSelector(chartToken: string) {
  return createSelector(
    createChartTypeSelector(chartToken),
    createEncodingSelector(chartToken),
    (type, encoding) => encoding != null && type != null && EncodingHelpers.isChartEncodingComplete(encoding, type)
  );
}

const isValidSelectors = new Map<string, any>();
export function createIsValidSelector(chartToken: string) {
  const selector = createSelector(
    createChartTypeSelector(chartToken),
    createEncodingSelector(chartToken),
    createQueryTokenSelector(chartToken),
    fromSwitchViewSelectors.getModel,
    (type, encoding, queryToken, model) => {
      let isValid = false;

      if (encoding != null && queryToken != null && model != null) {
        switch (type) {
          case 'table':
            isValid =
              EncodingHelpers.validateTableEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
              0;
            break;
          default:
            isValid =
              EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
              0;
            break;
        }
      }

      return isValid;
    }
  );

  if (isValidSelectors.has(chartToken)) {
    return isValidSelectors.get(chartToken) as typeof selector;
  } else {
    isValidSelectors.set(chartToken, selector);
    return selector;
  }
}

/**
 * renderDataModel selector that is not dependent on SelectedReport state.
 */
export function createRenderDataModelSelector(chartToken: string, planId: string) {
  return createSelector(
    fromFlamingoSelectors.createPlanSelector(planId),
    fromFlamingoSelectors.createResultSelector(planId),
    fromSwitchViewSelectors.getModel,
    createQueryTokenSelector(chartToken),
    (plan, result, model, queryToken) => {
      if (result != null && queryToken != null) {
        return SwitchRenderModel.make(model, queryToken, plan, result);
      }

      return null;
    }
  );
}

export function createFlamingoChartSummarySelector(token: string, planId: string) {
  return createSelector(
    createChartTypeEncodingSelector(token),
    createChartFormatSelector(token),
    createQueryTokenSelector(token),
    fromSwitchViewSelectors.getModel,
    fromFlamingoSelectors.createPlanSelector(planId),
    fromFlamingoSelectors.createLoadingSelector(planId),
    fromFlamingoSelectors.createResultSelector(planId),
    (typeEncoding, format, queryToken, model, plan, loading, result) => {
      if (typeEncoding != null && queryToken != null) {
        const { encoding, chartType } = typeEncoding;
        if (chartType !== 'table') {
          const isValid =
            EncodingHelpers.isChartEncodingComplete(encoding, chartType) &&
            EncodingHelpers.validateChartEncodingFields(encoding, SwitchSchemaModel.make(model, queryToken)).length <=
              0;

          if (isValid && !loading) {
            if (model != null && queryToken != null && plan != null && result != null && format != null) {
              const renderModel = SwitchRenderModel.make(model, queryToken, plan, result);
              if (renderModel != null) {
                return makeSummaryData(renderModel, encoding);
              }
            }

            return null; // null means we expected data, but there was none, i.e. invalid chart or plan failed
          }
        }
      }

      return undefined;
    }
  );
}

export function createCanSortChartSelector(chartToken: string) {
  return createSelector(
    createChartTypeSelector(chartToken),
    createEncodingSelector(chartToken),
    (chartType, encoding) => {
      if (encoding != null) {
        switch (chartType) {
          case 'pie':
          case 'donut':
            return (
              encoding.marks.properties.angle.values.length > 0 &&
              encoding.marks.properties.color.values.length > 0 &&
              encoding.marks.properties.color.values[0].variable.type === VariableType.DISCRETE
            );
          case 'bar':
          case 'hBar':
            // Don't allow sorting of a bar chart with only one measure value and nothing in color
            return (
              encoding.value.length > 1 ||
              (encoding.marks.series.length > 0 &&
                encoding.marks.series[0].properties.color.values.length > 0 &&
                encoding.marks.series[0].properties.color.values[0].formula !== Names.Key)
            );
          case 'bigValue':
            return encoding.y.length > 0 && encoding.marks.properties.text.values.length > 0;
          case 'vegas': {
            return EncodingHelpers.findSortByFields(encoding, chartType) !== undefined;
          }
          default: {
            const { primary, secondary } = EncodingHelpers.extractPrimarySecondaryAxes(encoding);
            return primary.length > 0 && secondary.length > 0 && secondary[0].variable.type === VariableType.DISCRETE;
          }
        }
      } else {
        return undefined;
      }
    }
  );
}
export function createCanSwapChartSelector(chartToken: string) {
  return createSelector(createChartTypeSelector(chartToken), (chartType) => {
    switch (chartType) {
      case 'hBar':
      case 'bar':
      case 'stackedBar':
      case 'hStackedBar':
      case 'stackedBar100':
      case 'hStackedBar100':
      case 'vegas':
      case 'vegasPivotTable':
        // A bit simple, but shouldn't be too bad
        return true;
      default:
        return false;
    }
  });
}

export function createTypeValidationSelector(chartToken: string) {
  return createSelector(
    createChartVersionSelector(chartToken),
    createEncodingSelector(chartToken),
    createChartTypeSelector(chartToken),
    createCanSortChartSelector(chartToken),
    createCanSwapChartSelector(chartToken),
    (version, encoding, chartType, canSort, canSwap) => {
      if (encoding != null && chartType != null) {
        return {
          canSort: version === 2 && canSort,
          canSwap: version === 2 && canSwap,
          isCompleteEncoding: version === 2 && EncodingHelpers.isChartEncodingComplete(encoding, chartType),
        };
      } else {
        return undefined;
      }
    }
  );
}

export function createModelValidationSelector(chartToken: string) {
  return createSelector(
    fromSwitchViewSelectors.getModel,
    createQueryTokenSelector(chartToken),
    createEncodingSelector(chartToken),
    (model, queryToken, encoding) => {
      if (model != null && queryToken != null && encoding != null) {
        const canHierarchy = EncodingHelpers.canChangeHierarchy(encoding, model, queryToken);

        return {
          canGoUp: canHierarchy.back,
          canGoDown: canHierarchy.forward,
        };
      }
    }
  );
}

export function createUndoSelector(chartToken: string) {
  return createSelector(createHistorySelector(chartToken), (history) => ({
    // null checking due to V1 charts having no history
    canRedo: history && history.redoStack.length > 0,
    canUndo: history && history.undoStack.length > 0,
    undoDesc: history && history.redoStack.length ? history.redoStack[0].action : '',
    redoDesc: history && history.undoStack.length ? history.undoStack[0].action : '',
  }));
}

export function createEncResultSelector(chartToken: string) {
  return createSelector(
    createChartTypeSelector(chartToken),
    createEncodingSelector(chartToken),
    createChartVersionSelector(chartToken),
    (chartType, encoding, version) => {
      let encResult: EncodingHelpers.EncResult;

      if (version === 2 && chartType != null) {
        encResult = new EncodingHelpers.EncResult();
        if (!encoding) {
          encResult = new EncodingHelpers.EncResult(EncodingHelpers.initEncoding(chartType));
        } else {
          encResult = new EncodingHelpers.EncResult(EncodingHelpers.loadEncoding(encoding));
        }
      } else {
        encResult = new EncodingHelpers.EncResult(EncodingHelpers.loadEncoding(EMPTY_ENCODING)); // Dummy encoding to avoid potential null reference errors
      }

      return encResult;
    }
  );
}

export function undoSelector(chartToken: string) {
  return createSelector(createHistorySelector(chartToken), (history) => ({
    // null checking due to V1 charts having no history
    canRedo: history && history.redoStack.length > 0,
    canUndo: history && history.undoStack.length > 0,
    undoDesc: history && history.redoStack.length ? history.redoStack[0].action : '',
    redoDesc: history && history.undoStack.length ? history.undoStack[0].action : '',
  }));
}

/**
 * Get all chart tokens related to a switch view token.
 */
export function createChartTokensBySwitchViewToken(switchViewToken: string) {
  return createSelector(selectSwitchViewTokens, (tokens) => {
    let chartTokens: string[] = [];
    tokens.forEach((svToken, cToken) => {
      if (svToken === switchViewToken) {
        chartTokens.push(cToken);
      }
    });

    return chartTokens;
  });
}

/**
 * Selector for chart tab metadata in query nav
 */
export function createChartsMetadataBySwitchViewToken(switchViewToken: string) {
  return createSelector(
    createChartTokensBySwitchViewToken(switchViewToken),
    selectTitles,
    selectChartTypes,
    selectVersions,
    selectV1Views,
    selectCreatedTimestamps,
    (chartTokens, titles, types, versions, v1Views, createdTimestamps) => {
      return chartTokens.map((chartToken) => {
        const viewVersion = versions.get(chartToken);
        const title = viewVersion === 1 ? v1Views.get(chartToken)?.chartTitle : titles.get(chartToken);
        return {
          chartToken,
          title,
          type: types.get(chartToken),
          viewVersion,
          legacyType: v1Views.get(chartToken)?.selectedChart,
          createdAt: createdTimestamps.get(chartToken),
        };
      });
    }
  );
}

/**
 * The following selectors exist only to support the legacy flow
 * Please do not use them or replicate this pattern
 *
 * TODO: remove these selectors when SelectedReportState is gone
 *
 */

export function createChartBaseSelector1(chartToken: string) {
  return createSelector(
    createEncodingSelector(chartToken),
    createEncResultSelector(chartToken),
    createChartTypeSelector(chartToken),
    createChartTitleSelector(chartToken),
    createChartDescriptionSelector(chartToken),
    (encoding, encResult, chartType, title, description) => ({
      encoding,
      encResult,
      chartType,
      title,
      description,
    })
  );
}

export function createChartBaseSelector2(chartToken: string) {
  return createSelector(
    createPaletteSelector(chartToken),
    createChartFormatSelector(chartToken),
    createChartFieldFormatMapSelector(chartToken),
    createChartFieldColorMapSelector(chartToken),
    createChartFacetSizeMapSelector(chartToken),
    createHistorySelector(chartToken),
    createQueryTokenSelector(chartToken),
    createSwitchViewTokenSelector(chartToken),
    createUndoSelector(chartToken),
    createIsAutoUpdateDisabledSelector(chartToken),
    createCaptionSelector(chartToken),
    (
      colorPaletteToken,
      format,
      fieldFormats,
      fieldColors,
      facetSizes,
      history,
      queryToken,
      switchViewToken,
      undo,
      isAutoUpdateDisabled,
      caption
    ) => ({
      colorPaletteToken,
      format,
      fieldFormats,
      fieldColors,
      facetSizes,
      history,
      queryToken,
      switchViewToken,
      ...undo,
      isAutoUpdateDisabled,
      caption,
    })
  );
}

export function createChartBaseSelector3(chartToken: string) {
  return createSelector(
    createChartResourceSelector(chartToken),
    createChartVersionSelector(chartToken),
    createV1ViewSelector(chartToken),
    fromSwitchViewSelectors.getModel,
    createTypeValidationSelector(chartToken),
    createModelValidationSelector(chartToken),
    (resource, version, v1View, model, typeValidations, modelValidations) => ({
      resource,
      version,
      v1View,
      token: chartToken,
      model: model,
      ...typeValidations,
      ...modelValidations,
    })
  );
}

export function createChartSelector(chartToken: string) {
  return createSelector(
    createChartBaseSelector1(chartToken),
    createChartBaseSelector2(chartToken),
    createChartBaseSelector3(chartToken),
    (chartBase1, chartBase2, chartBase3) => ({
      ...chartBase1,
      ...chartBase2,
      ...chartBase3,
    })
  );
}

export function createCoreChartSelector(chartToken: string) {
  return createStructuredSelector({
    token: () => chartToken,
    resource: createChartResourceSelector(chartToken),
    queryToken: createQueryTokenSelector(chartToken),
    switchViewToken: createSwitchViewTokenSelector(chartToken),
    version: createChartVersionSelector(chartToken),
    v1View: createV1ViewSelector(chartToken),
    history: createHistorySelector(chartToken),
    format: createChartFormatSelector(chartToken),
    fieldFormats: createChartFieldFormatMapSelector(chartToken),
    fieldColors: createChartFieldColorMapSelector(chartToken),
    facetSizes: createChartFacetSizeMapSelector(chartToken),
    chartType: createChartTypeSelector(chartToken),
    title: createChartTitleSelector(chartToken),
    description: createChartDescriptionSelector(chartToken),
    caption: createCaptionSelector(chartToken),
    colorPaletteToken: createPaletteSelector(chartToken),
    isAutoUpdateDisabled: createIsAutoUpdateDisabledSelector(chartToken),
  });
}

export function createAddSelectedFieldToFormatSelector() {
  return createSelector(selectSelectedFieldToFormat, (selectedField) => selectedField);
}
